import React, { useCallback, useContext, useEffect, useState } from "react";
import { Menu, type MenuProps, theme } from "antd";
import Sider from "antd/es/layout/Sider";
import { useNavigate, useLocation } from "react-router-dom";
import { RiBrainLine, RiHome2Fill, RiInfinityFill } from "react-icons/ri";
import { GoHome } from "react-icons/go";
import { FaJava, FaLaptopCode, FaBookOpen } from "react-icons/fa";
import { PiCodeBlock, PiStudent, PiExam } from "react-icons/pi";
import { AuthContext } from "../../../config/AuthContext";
import { FaRankingStar } from "react-icons/fa6";
import { TbPokeball } from "react-icons/tb";
import { VscCode } from "react-icons/vsc";
import {
    DesktopOutlined,
    AreaChartOutlined,
    TrophyOutlined,
    SkinOutlined,
    UserOutlined,
    TeamOutlined,
    CompassOutlined,
    GiftOutlined
} from "@ant-design/icons";
import { GetSceneActivities } from "../../../dao/jutge/GetJavamonConfig";
import { GetAlumneId } from "../../../dao/GetAlumneId";
import UseWebSocketSubscription from "../../UseWebSocketSubscription";

interface Props {
    currentVal: number,
    setPantalla: (value: number) => void;
}

const AppSider = (props: Props) => {
    const [pantalla, setPantalla] = useState(props.currentVal);
    const { isLoggedIn, userId, user, setBreadCrumb, alumne, setAlumneInfo } = useContext(AuthContext);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        document.title = 'Web Dubtes';
    }, []);

    // Fetch active RPG status
    const [hasActiveRpg, setHasActiveRpg] = useState<boolean>(false);

    useEffect(() => {
        let mounted = true;
        async function checkActiveRpg() {
            try {
                const activities = await GetSceneActivities();
                if (!mounted) return;
                if (activities && activities.length > 0) {
                    const uniqueAulas = Array.from(new Set(activities.map((a: any) => a.id_aula)));
                    if (user?.tipus === "0" || user?.tipus === "1") {
                        // For teachers/admins, if any scene activity exists
                        setHasActiveRpg(true);
                    } else {
                        // For students, check their classrooms
                        let userAulas = alumne?.aules || [];
                        const activeRpg = (userAulas || []).some((aula: any) => {
                            const aulaId = Number(aula.id || aula.id_aula);
                            return uniqueAulas.includes(aulaId) && (aula.jocHabilitat === undefined || aula.jocHabilitat === 1 || aula.jocHabilitat === true);
                        });
                        setHasActiveRpg(activeRpg);
                    }
                } else {
                    setHasActiveRpg(false);
                }
            } catch (err) {
                console.error("Error checking active RPG classrooms in AppSider:", err);
                if (mounted) setHasActiveRpg(false);
            }
        }
        if (isLoggedIn) {
            checkActiveRpg();
        }
        return () => { mounted = false; };
    }, [isLoggedIn, userId, user]);

    // Check if user has IDE access
    const hasIde = alumne?.alumne?.ide === 1;
    const isRole3 = String(user?.tipus) === "3";

    // --- ROLE 3 (OPENSOURCE / PROBLEMES ONLY) MENU ---
    const icons3 = [FaJava];
    const text3 = ['Problemes'];
    const href3 = ['/jutge/problemes'];
    if (hasIde) {
        icons3.push(VscCode);
        text3.push('IDE JAVA');
        href3.push('/jutge/ide');
    }

    const items3: MenuProps['items'] = icons3.map(
        (icon, index) => ({
            key: String(index + 1),
            icon: React.createElement(icon),
            label: text3[index],
            onClick: () => {
                navigate(href3[index]);
            }
        })
    );

    // Check if any of student's classrooms is in Exam Mode (is_examen === 1)
    const [hasExamMode, setHasExamMode] = useState<boolean>(false);

    const evaluateExamMode = useCallback((aulesList: any[]) => {
        if (user?.tipus === "0" || user?.tipus === "1") {
            setHasExamMode(false);
            return;
        }
        const examActive = (aulesList || []).some((aula: any) =>
            Boolean(aula.isExamen === 1 || aula.isExamen === true || aula.is_examen === 1 || aula.is_examen === true)
        );
        setHasExamMode(examActive);

        // Si el aula está en modo examen y el alumno está en cualquier pantalla de jutge que no sea /jutge/examens, redirigir directamente a /jutge/examens
        if (examActive && user?.tipus === "2") {
            const path = window.location.pathname;
            if (path.startsWith('/jutge') && !path.startsWith('/jutge/examens')) {
                navigate('/jutge/examens');
            }
        }
    }, [user, navigate]);

    useEffect(() => {
        if (isLoggedIn) {
            evaluateExamMode(alumne?.aules || []);
        }
    }, [isLoggedIn, alumne?.aules, evaluateExamMode]);

    // WebSocket subscription para reaccionar al cambio de modo examen en tiempo real
    UseWebSocketSubscription(["/topic/home"], async () => {
        if (userId && setAlumneInfo) {
            try {
                const freshAlumne = await GetAlumneId(userId);
                if (freshAlumne?.aules) {
                    setAlumneInfo(freshAlumne);
                    evaluateExamMode(freshAlumne.aules);
                }
            } catch (e) {
                console.error("Error refreshing alumne on websocket in AppSider:", e);
            }
        }
    });

    // --- STUDENT MENU ---
    // Cuando el aula está en modo examen, SOLO existe la pestaña 'Exàmens' para el alumno
    const rawStudentMenu = hasExamMode ? [
        { icon: PiExam, text: 'Exàmens', href: '/jutge/examens' }
    ] : [
        { icon: GoHome, text: 'Home', href: '/jutge' },
        { icon: FaJava, text: 'Problemes', href: '/jutge/problemes' },
        { icon: PiCodeBlock, text: 'Activitats', href: '/jutge/activitats' },
        { icon: FaLaptopCode, text: 'Practiques', href: '/jutge/practiques' },
        { icon: PiExam, text: 'Exàmens', href: '/jutge/examens' },
        ...(hasActiveRpg ? [{ icon: FaRankingStar, text: 'Ranking', href: '/jutge/ranking' }] : []),
        { icon: FaBookOpen, text: 'Píldoras', href: '/jutge/pildores' },
        ...(hasIde ? [{ icon: VscCode, text: 'IDE JAVA', href: '/jutge/ide' }] : [])
    ];

    const items2: MenuProps['items'] = rawStudentMenu.map((item, index) => ({
        key: item.text,
        icon: React.createElement(item.icon),
        label: item.text,
        onClick: () => navigate(item.href)
    }));

    // --- TEACHER MENU ---
    const rawTeacherMenu: any[] = [
        { icon: GoHome, text: 'Home', href: '/jutge' },
        { icon: FaJava, text: 'Problemes', href: '/jutge/problemes' },
        { icon: PiCodeBlock, text: 'Activitats', href: '/jutge/activitats' },
        { icon: FaLaptopCode, text: 'Practiques', href: '/jutge/practiques' },
        { icon: PiExam, text: 'Exàmens', href: '/jutge/examens' },
        ...(hasActiveRpg ? [{ icon: FaRankingStar, text: 'Ranking', href: '/jutge/ranking' }] : []),
        { icon: FaBookOpen, text: 'Píldoras', href: '/jutge/pildores' },
        { icon: PiStudent, text: 'Notes', href: '/jutge/notes' },
    ];

    if (user.tipus === "0" || user.tipus === "1") {
        rawTeacherMenu.push({ icon: TbPokeball, text: 'Javamons', href: '/jutge/javamon/config' });
    }

    if (hasIde) {
        rawTeacherMenu.push({ icon: VscCode, text: 'IDE JAVA', href: '/jutge/ide' });
    }

    const itemsT: MenuProps['items'] = rawTeacherMenu.map((item) => {
        if (item.text === 'IDE JAVA') {
            return {
                key: 'IDE JAVA',
                icon: React.createElement(item.icon),
                label: item.text,
                children: [
                    {
                        key: 'IDE JAVA-2',
                        icon: <DesktopOutlined />,
                        label: 'Supervisor',
                        onClick: () => navigate('/jutge/ide/admin')
                    },
                    {
                        key: 'IDE JAVA-3',
                        icon: <AreaChartOutlined />,
                        label: 'Monitoreo',
                        onClick: () => navigate('/jutge/ide/telemetry')
                    }
                ]
            };
        }

        return {
            key: item.text,
            icon: React.createElement(item.icon),
            label: item.text,
            onClick: () => navigate(item.href)
        };
    });

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Map current route or currentVal to menu key
    const currentValToText: Record<number, string> = {
        0: 'Home',
        1: 'Problemes',
        2: 'Activitats',
        3: 'Practiques',
        4: 'Ranking',
        5: 'Píldoras',
        6: 'Notes',
        7: 'Javamons',
        8: 'IDE JAVA',
        9: 'Exàmens'
    };

    let activeKey = currentValToText[props.currentVal] || String(props.currentVal + 1);

    if (isRole3) {
        activeKey = props.currentVal === 1 ? "1" : (props.currentVal + 1) + "";
    } else {
        // Find by path if matching
        const currentPath = location.pathname.toLowerCase();
        if (currentPath.includes('/jutge/ide/telemetry')) {
            activeKey = 'IDE JAVA-3';
        } else if (currentPath.includes('/jutge/ide/admin')) {
            activeKey = 'IDE JAVA-2';
        } else if (currentPath.includes('/jutge/ide')) {
            activeKey = 'IDE JAVA';
        } else if (currentPath.includes('/jutge/javamon/config')) {
            activeKey = 'Javamons';
        } else if (currentPath.includes('/jutge/notes')) {
            activeKey = 'Notes';
        } else if (currentPath.includes('/jutge/pildores') || currentPath.includes('/jutge/caza')) {
            activeKey = 'Píldoras';
        } else if (currentPath.includes('/jutge/ranking')) {
            activeKey = 'Ranking';
        } else if (currentPath.includes('/jutge/examens')) {
            activeKey = 'Exàmens';
        } else if (currentPath.includes('/jutge/practiques')) {
            activeKey = 'Practiques';
        } else if (currentPath.includes('/jutge/activitats')) {
            activeKey = 'Activitats';
        } else if (currentPath.includes('/jutge/problemes')) {
            activeKey = 'Problemes';
        } else if (currentPath === '/jutge' || currentPath === '/jutge/') {
            activeKey = 'Home';
        }
    }

    const isIdeSubmenu = location.pathname.includes('/jutge/ide/admin') || location.pathname.includes('/jutge/ide/telemetry');
    const [openKeys, setOpenKeys] = useState<string[]>(isIdeSubmenu ? ['IDE JAVA'] : []);

    useEffect(() => {
        if (isIdeSubmenu) {
            setOpenKeys(prev => prev.includes('IDE JAVA') ? prev : [...prev, 'IDE JAVA']);
        }
    }, [location.pathname, isIdeSubmenu]);

    return (
        <Sider style={{ background: colorBgContainer }} width={200}>
            <Menu
                mode="inline"
                selectedKeys={[activeKey]}
                openKeys={openKeys}
                onOpenChange={(keys) => setOpenKeys(keys as string[])}
                style={{ height: '100%' }}
                items={isRole3 ? items3 : (user.tipus === "0" || user.tipus === "1" ? itemsT : items2)}
            />
        </Sider>
    );
};

export default AppSider;